import React, { useState, useEffect } from 'react';
import { Spinner, Modal } from 'react-bootstrap';
import InputMask from 'react-input-mask';
import { useUserContext } from '../../UserContext';
import CurrencyInput from 'react-currency-input-field';

/* ── ícone reutilizável ── */
const Icon = ({ d, size = 16, color = 'currentColor', stroke = 2 }) => (
  <svg width={size} height={size} fill="none" stroke={color} viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={stroke} d={d} />
  </svg>
);

const ICONS = {
  plus:   'M12 4v16m8-8H4',
  edit:   'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
  trash:  'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
  launch: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
  user:   'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
  info:   'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  check:  'M5 13l4 4L19 7',
  close:  'M6 18L18 6M6 6l12 12',
  lock:   'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
};

/* ── label de campo ── */
const FieldLabel = ({ children, required }) => (
  <label style={{
    fontSize: `calc(11px * var(--font-scale))`, fontWeight: '700', color: '#6b7280',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    display: 'block', marginBottom: '5px',
  }}>
    {children}
    {required && <span style={{ color: '#ef4444', marginLeft: '2px' }}>*</span>}
  </label>
);

/* ── estilos compartilhados ── */
const inputStyle = {
  width: '100%', padding: '9px 12px', borderRadius: '8px',
  border: '1px solid #e5e7eb', fontSize: `calc(13px * var(--font-scale))`, color: '#374151',
  fontFamily: "'Inter','Segoe UI',sans-serif", outline: 'none',
  backgroundColor: '#fff', boxSizing: 'border-box',
  transition: 'border-color 0.15s, box-shadow 0.15s',
};

/* ── handlers de foco para elementos nativos ── */
const focusHandlers = {
  onFocus: e => {
    e.target.style.borderColor = '#2563eb';
    e.target.style.boxShadow   = '0 0 0 3px rgba(37,99,235,0.1)';
  },
  onBlur: e => {
    e.target.style.borderColor = '#e5e7eb';
    e.target.style.boxShadow   = 'none';
  },
};

/*
 * ── ATENÇÃO: para o react-input-mask, onFocus/onBlur devem ser passados
 *    diretamente na tag <InputMask>, NÃO no <input> filho via spread.
 *    Por isso criamos maskFocusHandlers separados que manipulam o target
 *    correto (o input interno é acessado via e.target normalmente).
 */
const maskFocusHandlers = {
  onFocus: e => {
    e.target.style.borderColor = '#2563eb';
    e.target.style.boxShadow   = '0 0 0 3px rgba(37,99,235,0.1)';
  },
  onBlur: e => {
    e.target.style.borderColor = '#e5e7eb';
    e.target.style.boxShadow   = 'none';
  },
};

/* ── toggle switch ── */
const SwitchField = ({ id, label, checked, onChange }) => (
  <label htmlFor={id} style={{
    display: 'flex', alignItems: 'center', gap: '10px',
    cursor: 'pointer', userSelect: 'none',
    fontSize: `calc(13px * var(--font-scale))`, color: '#374151', fontWeight: '500',
  }}>
    <div style={{ position: 'relative', width: '40px', height: '22px', flexShrink: 0 }}>
      <input
        id={id} type="checkbox" checked={checked} onChange={onChange}
        style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
      />
      <div style={{
        position: 'absolute', inset: 0, borderRadius: '999px',
        backgroundColor: checked ? '#2563eb' : '#d1d5db',
        transition: 'background 0.2s',
      }} />
      <div style={{
        position: 'absolute', top: '3px',
        left: checked ? '21px' : '3px',
        width: '16px', height: '16px', borderRadius: '50%',
        backgroundColor: '#fff', transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </div>
    {label}
  </label>
);

/* ── separador de seção no modal ── */
const SectionTitle = ({ icon, label }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: '8px',
    marginBottom: '10px', marginTop: '4px',
    paddingBottom: '6px', borderBottom: '1px solid #f1f5f9',
  }}>
    <Icon d={icon} size={13} color="#2563eb" />
    <span style={{
      fontSize: `calc(11px * var(--font-scale))`, fontWeight: '700', color: '#6b7280',
      textTransform: 'uppercase', letterSpacing: '0.6px',
    }}>
      {label}
    </span>
  </div>
);

/* ════════════════════════════════════════════════════════ */

const ColaboradoresTab = ({
  allFuncionarios, isLoadingFuncionarios, funcionarioMessage, setFuncionarioMessage,
  handleOpenFuncionarioModal, handleDeleteFuncionario, deletingFuncionario,
  handleOpenNovoLancamentoModal,
  showFuncionarioModal, handleCloseFuncionarioModal, isEditingFuncionario,
  newFuncionarioData, creatingFuncionario,
  selectedSetorModal, setSelectedSetorModal,
  setores, cargosDoSetorModal, isLoadingCargosModal,
  especialidades, setNewFuncionarioData,
  handleSaveFuncionario: originalHandleSave,
  perfis,
}) => {
  const { currentUserRole } = useUserContext();

  const NOME_SETOR_CORPO_CLINICO = 'Corpo Clínico';
  const NOME_PERFIL_MEDICO       = 'Médico';
  const perfilMedico = (perfis || []).find(
    p => p.tipo === 'padrao' && p.nome === NOME_PERFIL_MEDICO
  );

  /* ── handlers ── */
  const handleLocalInputChange = e => {
    const { name, value } = e.target;
    if (name === 'telefoneCompleto') {
      const nums = value.replace(/\D/g, '');
      setNewFuncionarioData(prev => ({
        ...prev,
        telefoneCompleto: value,
        dddcelular: nums.substring(0, 2),
        celular:    nums.substring(2),
      }));
    } else {
      setNewFuncionarioData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSalaryChange = (value, name) => {
    setNewFuncionarioData(prev => ({ ...prev, [name]: value || '' }));
  };

  const handleSaveFuncionario = () => {
    const dataToSave = { ...newFuncionarioData };
    if (dataToSave.salario_base && typeof dataToSave.salario_base === 'string') {
      const clean = dataToSave.salario_base
        .replace('R$ ', '').replace(/\./g, '').replace(',', '.');
      const val = parseFloat(clean);
      dataToSave.salario_base = !isNaN(val) ? val : null;
    } else {
      dataToSave.salario_base = Number(dataToSave.salario_base) || null;
    }
    if (!dataToSave.tipo_contrato) dataToSave.tipo_contrato = 'CLT';
    originalHandleSave(dataToSave);
  };

  const handlePerfilChange = e => {
    const value = e.target.value;
    if (!value) {
      setNewFuncionarioData(prev => ({
        ...prev, entidade_perfil_id: null, standard_role_id: null,
      }));
      return;
    }
    const [tipo, idStr] = value.split('|');
    const id = parseInt(idStr, 10);
    if (tipo === 'padrao') {
      setNewFuncionarioData(prev => ({
        ...prev, standard_role_id: id, entidade_perfil_id: null,
      }));
    } else if (tipo === 'customizado') {
      setNewFuncionarioData(prev => ({
        ...prev, entidade_perfil_id: id, standard_role_id: null,
      }));
    }
  };

  const [isProfileSelectDisabled, setIsProfileSelectDisabled] = useState(false);

  useEffect(() => {
    if (!showFuncionarioModal || !setores.length || !(perfis || []).length) return;
    const selectedSetorObj = setores.find(
      s => s.id?.toString() === selectedSetorModal?.toString()
    );
    if (selectedSetorModal && !selectedSetorObj) {
      setIsProfileSelectDisabled(false); return;
    }
    if (!perfilMedico) { setIsProfileSelectDisabled(false); return; }

    if (selectedSetorObj?.nome === NOME_SETOR_CORPO_CLINICO) {
      setIsProfileSelectDisabled(true);
      if (
        newFuncionarioData.standard_role_id !== perfilMedico.id ||
        newFuncionarioData.entidade_perfil_id !== null
      ) {
        setNewFuncionarioData(prev => ({
          ...prev, standard_role_id: perfilMedico.id, entidade_perfil_id: null,
        }));
      }
    } else {
      setIsProfileSelectDisabled(false);
      if (perfilMedico && newFuncionarioData.standard_role_id === perfilMedico.id) {
        setNewFuncionarioData(prev => ({
          ...prev, standard_role_id: null, entidade_perfil_id: null,
        }));
      }
    }
  }, [
    selectedSetorModal, setores, perfis, setNewFuncionarioData, showFuncionarioModal,
    newFuncionarioData.standard_role_id, newFuncionarioData.entidade_perfil_id,
    NOME_SETOR_CORPO_CLINICO, NOME_PERFIL_MEDICO, isEditingFuncionario,
  ]);

  const selectStyle = { ...inputStyle, appearance: 'auto' };

  /* ════════════════════ RENDER ════════════════════ */
  return (
    <div style={{ fontFamily: "'Inter','Segoe UI',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        .colab-table { width: 100%; border-collapse: collapse; }
        .colab-table th { padding: 10px 14px; font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 2px solid #e2e8f0; background: linear-gradient(135deg,#f8fafc,#f1f5f9); text-align: left; }
        .colab-table td { padding: 11px 14px; font-size: calc(13px * var(--font-scale)) color: #374151; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
        .colab-table tbody tr:hover { background: #fafbff; }
        .colab-action-btn { display:inline-flex; align-items:center; gap:5px; padding:5px 10px; border-radius:6px; border:none; font-size:12px; font-weight:600; cursor:pointer; font-family:inherit; transition:opacity 0.15s; }
        .colab-action-btn:disabled { opacity:0.5; cursor:not-allowed; }
        @media (max-width:767px) { .hide-md { display:none!important; } }
        @media (max-width:575px) { .hide-sm { display:none!important; } }
      `}</style>

      {/* ── CARD DE DICAS ── */}
      <div style={{
        backgroundColor: '#eff6ff', border: '1px solid #bfdbfe',
        borderRadius: '12px', padding: '16px 20px', marginBottom: '20px',
        display: 'flex', gap: '12px',
      }}>
        <div style={{ flexShrink: 0, marginTop: '1px' }}>
          <Icon d={ICONS.info} size={16} color="#2563eb" />
        </div>
        <div>
          <p style={{ margin: '0 0 6px', fontSize: `calc(12px * var(--font-scale))`, fontWeight: '700', color: '#1e40af' }}>
            Dicas de Uso
          </p>
          <ul style={{ margin: 0, paddingLeft: '16px', fontSize: `calc(12px * var(--font-scale))`, color: '#374151', lineHeight: '1.7' }}>
            <li><strong>Lançamentos:</strong> Registre proventos (bônus, comissões) ou descontos (faltas, adiantamentos) na folha de pagamento.</li>
            <li><strong>Salário Base:</strong> Valor fundamental usado como referência para gerar a folha de pagamento mensal.</li>
          </ul>
        </div>
      </div>

      {/* ── CABEÇALHO ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div>
          <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#111827' }}>
            Colaboradores
          </h5>
          <p style={{ margin: '2px 0 0', fontSize: `calc(12px * var(--font-scale))`, color: '#9ca3af' }}>
            {allFuncionarios.length}{' '}
            {allFuncionarios.length === 1 ? 'funcionário cadastrado' : 'funcionários cadastrados'}
          </p>
        </div>
        <button
          onClick={() => handleOpenFuncionarioModal()}
          style={{
            display: 'flex', alignItems: 'center', gap: '7px',
            padding: '9px 18px', borderRadius: '8px', border: 'none',
            background: 'linear-gradient(135deg, #1e3a5f, #2563eb)',
            color: '#fff', fontSize: `calc(13px * var(--font-scale))`, fontWeight: '600',
            cursor: 'pointer', boxShadow: '0 2px 8px rgba(37,99,235,0.3)',
            fontFamily: 'inherit',
          }}
        >
          <Icon d={ICONS.plus} size={14} color="#fff" />
          Novo Funcionário
        </button>
      </div>

      {/* ── ALERTA ── */}
      {funcionarioMessage && (
        <div style={{
          padding: '12px 16px', borderRadius: '8px', marginBottom: '16px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
          backgroundColor: funcionarioMessage.type === 'danger'  ? '#fef2f2'
                          : funcionarioMessage.type === 'success' ? '#f0fdf4' : '#fffbeb',
          border: `1px solid ${
            funcionarioMessage.type === 'danger'  ? '#fecaca'
            : funcionarioMessage.type === 'success' ? '#bbf7d0' : '#fde68a'
          }`,
          color: funcionarioMessage.type === 'danger'  ? '#dc2626'
               : funcionarioMessage.type === 'success' ? '#16a34a' : '#d97706',
          fontSize: `calc(13px * var(--font-scale))`,
        }}>
          <span>{funcionarioMessage.text}</span>
          <button
            onClick={() => setFuncionarioMessage(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: '16px', lineHeight: 1, padding: 0 }}
          >×</button>
        </div>
      )}

      {/* ── TABELA ── */}
      <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        {isLoadingFuncionarios ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px', gap: '12px' }}>
            <Spinner animation="border" variant="primary" style={{ width: '32px', height: '32px' }} />
            <p style={{ margin: 0, fontSize: `calc(13px * var(--font-scale))`, color: '#6b7280' }}>Carregando colaboradores...</p>
          </div>
        ) : allFuncionarios.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center' }}>
            <Icon d={ICONS.user} size={36} color="#d1d5db" />
            <p style={{ margin: '12px 0 0', fontSize: `calc(14px * var(--font-scale))`, color: '#9ca3af', fontWeight: '500' }}>
              Nenhum funcionário cadastrado
            </p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="colab-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th className="hide-md">CPF</th>
                  <th className="hide-md">Email</th>
                  <th>Setor</th>
                  <th className="hide-sm">Cargo</th>
                  <th style={{ textAlign: 'center' }}>Comissão</th>
                  <th style={{ textAlign: 'center' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {allFuncionarios.map(funcionario => (
                  <tr key={funcionario.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                          width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
                          background: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <span style={{ fontSize: `calc(12px * var(--font-scale))`, fontWeight: '700', color: '#2563eb' }}>
                            {(funcionario.nome || '?')[0].toUpperCase()}
                          </span>
                        </div>
                        <span style={{ fontWeight: '600', color: '#111827' }}>{funcionario.nome}</span>
                      </div>
                    </td>
                    <td className="hide-md" style={{ color: '#6b7280', fontFamily: 'monospace', fontSize: `calc(12px * var(--font-scale))` }}>
                      {funcionario.cpf}
                    </td>
                    <td className="hide-md" style={{ color: '#6b7280' }}>{funcionario.email}</td>
                    <td>
                      <span style={{
                        fontSize: `calc(11px * var(--font-scale))`, fontWeight: '600', padding: '2px 8px', borderRadius: '999px',
                        backgroundColor: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0',
                      }}>
                        {funcionario.cargo?.setor?.nome || 'N/A'}
                      </span>
                    </td>
                    <td className="hide-sm">{funcionario.cargo?.nome || 'N/A'}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        fontSize: `calc(11px * var(--font-scale))`, fontWeight: '700', padding: '2px 8px', borderRadius: '999px',
                        backgroundColor: funcionario.comissionavel ? '#f0fdf4' : '#f9fafb',
                        color:           funcionario.comissionavel ? '#16a34a'  : '#9ca3af',
                        border: `1px solid ${funcionario.comissionavel ? '#bbf7d0' : '#e5e7eb'}`,
                      }}>
                        <Icon
                          d={funcionario.comissionavel ? ICONS.check : ICONS.close}
                          size={10}
                          color={funcionario.comissionavel ? '#16a34a' : '#9ca3af'}
                          stroke={2.5}
                        />
                        {funcionario.comissionavel ? 'Sim' : 'Não'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        <button
                          className="colab-action-btn"
                          onClick={() => handleOpenFuncionarioModal(funcionario)}
                          style={{ backgroundColor: '#fffbeb', color: '#d97706', border: '1px solid #fde68a' }}
                          title="Editar"
                        >
                          <Icon d={ICONS.edit} size={12} color="#d97706" />
                          <span className="hide-sm">Editar</span>
                        </button>
                        <button
                          className="colab-action-btn"
                          onClick={() => handleDeleteFuncionario(funcionario.id)}
                          disabled={deletingFuncionario}
                          style={{ backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}
                          title="Excluir"
                        >
                          {deletingFuncionario
                            ? <Spinner as="span" animation="border" size="sm" style={{ width: '12px', height: '12px', borderWidth: '2px' }} />
                            : <Icon d={ICONS.trash} size={12} color="#dc2626" />
                          }
                          <span className="hide-sm">Excluir</span>
                        </button>
                        <button
                          className="colab-action-btn"
                          onClick={() => handleOpenNovoLancamentoModal(funcionario)}
                          style={{ backgroundColor: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }}
                          title="Lançamentos"
                        >
                          <Icon d={ICONS.launch} size={12} color="#2563eb" />
                          <span className="hide-sm">Lançamentos</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ════════════ MODAL: CRIAR / EDITAR FUNCIONÁRIO ════════════ */}
      <Modal show={showFuncionarioModal} onHide={handleCloseFuncionarioModal} size="lg" centered>
        <Modal.Header style={{ padding: 0, border: 'none' }}>
          <div style={{
            width: '100%', padding: '20px 24px',
            background: 'linear-gradient(135deg, #1e3a5f, #2563eb)',
            display: 'flex', alignItems: 'center', gap: '12px',
          }}>
            <div style={{
              width: '40px', height: '40px', borderRadius: '10px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <Icon d={isEditingFuncionario ? ICONS.edit : ICONS.plus} size={18} color="#fff" />
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ margin: 0, fontSize: `calc(15px * var(--font-scale))`, fontWeight: '700', color: '#fff' }}>
                {isEditingFuncionario ? 'Editar Funcionário' : 'Novo Funcionário'}
              </p>
              <p style={{ margin: 0, fontSize: `calc(11px * var(--font-scale))`, color: 'rgba(255,255,255,0.7)' }}>
                Preencha os dados do colaborador
              </p>
            </div>
            <button
              onClick={handleCloseFuncionarioModal}
              style={{
                width: '28px', height: '28px', borderRadius: '6px',
                border: '1px solid rgba(255,255,255,0.3)',
                backgroundColor: 'rgba(255,255,255,0.15)',
                color: '#fff', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '18px', lineHeight: 1, flexShrink: 0,
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.25)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)'}
            >×</button>
          </div>
        </Modal.Header>

        <Modal.Body style={{ padding: '24px', backgroundColor: '#f9fafb' }}>
          {funcionarioMessage && (
            <div style={{
              padding: '10px 14px', borderRadius: '8px', marginBottom: '16px',
              backgroundColor: funcionarioMessage.type === 'danger' ? '#fef2f2' : '#f0fdf4',
              border: `1px solid ${funcionarioMessage.type === 'danger' ? '#fecaca' : '#bbf7d0'}`,
              color: funcionarioMessage.type === 'danger' ? '#dc2626' : '#16a34a',
              fontSize: `calc(13px * var(--font-scale))`, display: 'flex', justifyContent: 'space-between',
            }}>
              <span>{funcionarioMessage.text}</span>
              <button
                onClick={() => setFuncionarioMessage(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: '16px', lineHeight: 1, padding: 0 }}
              >×</button>
            </div>
          )}

          <form onSubmit={e => { e.preventDefault(); handleSaveFuncionario(); }}>

            {/* ── Cargo e Contrato ── */}
            <SectionTitle icon={ICONS.user} label="Cargo e Contrato" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px', marginBottom: '14px' }}>
              <div>
                <FieldLabel required>Setor</FieldLabel>
                <select
                  style={selectStyle} value={selectedSetorModal} required
                  onChange={e => {
                    setSelectedSetorModal(e.target.value);
                    setNewFuncionarioData(prev => ({ ...prev, cargo_id: '' }));
                  }}
                  {...focusHandlers}
                >
                  <option value="">Selecione um Setor</option>
                  {setores.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
                </select>
              </div>
              <div>
                <FieldLabel required>Cargo</FieldLabel>
                <select
                  style={{ ...selectStyle, opacity: (!selectedSetorModal || isLoadingCargosModal) ? 0.6 : 1 }}
                  name="cargo_id" value={newFuncionarioData.cargo_id}
                  onChange={handleLocalInputChange} required
                  disabled={!selectedSetorModal || isLoadingCargosModal}
                  {...focusHandlers}
                >
                  <option value="">
                    {isLoadingCargosModal ? 'Carregando...'
                      : selectedSetorModal ? 'Selecione um Cargo' : 'Selecione um Setor primeiro'}
                  </option>
                  {cargosDoSetorModal.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
              </div>
              <div>
                <FieldLabel required>Tipo de Contrato</FieldLabel>
                <select
                  style={selectStyle} name="tipo_contrato"
                  value={newFuncionarioData.tipo_contrato || 'CLT'}
                  onChange={handleLocalInputChange} required
                  {...focusHandlers}
                >
                  <option value="">Selecione...</option>
                  <option value="CLT">CLT</option>
                  <option value="PJ">PJ (Pessoa Jurídica)</option>
                </select>
              </div>
            </div>

            {/* Especialidade (condicional) */}
            {newFuncionarioData.cargo_id &&
              especialidades.some(e => e.cargo_id === parseInt(newFuncionarioData.cargo_id)) && (
              <div style={{ marginBottom: '14px', maxWidth: '260px' }}>
                <FieldLabel>Especialidade</FieldLabel>
                <select
                  style={selectStyle} name="especialidade_id"
                  value={newFuncionarioData.especialidade_id || ''}
                  onChange={handleLocalInputChange}
                  {...focusHandlers}
                >
                  <option value="">Selecione</option>
                  {especialidades
                    .filter(e => e.cargo_id === parseInt(newFuncionarioData.cargo_id))
                    .map(e => <option key={e.id} value={e.id}>{e.nome}</option>)
                  }
                </select>
              </div>
            )}

            {/* ── Dados Pessoais ── */}
            <SectionTitle icon={ICONS.user} label="Dados Pessoais" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px', marginBottom: '14px' }}>
              <div style={{ gridColumn: 'span 2' }}>
                <FieldLabel required>Nome Completo</FieldLabel>
                <input
                  style={inputStyle} type="text" name="nome"
                  value={newFuncionarioData.nome}
                  onChange={handleLocalInputChange} required
                  {...focusHandlers}
                />
              </div>

              {/* ── CELULAR: focusHandlers na tag InputMask, NÃO no input filho ── */}
              <div>
                <FieldLabel required>Celular (com DDD)</FieldLabel>
                <InputMask
                  mask="(99) 99999-9999"
                  value={
                    newFuncionarioData.telefoneCompleto !== undefined
                      ? newFuncionarioData.telefoneCompleto
                      : `${newFuncionarioData.dddcelular || ''}${newFuncionarioData.celular || ''}`
                  }
                  onChange={handleLocalInputChange}
                  name="telefoneCompleto"
                  onFocus={maskFocusHandlers.onFocus}
                  onBlur={maskFocusHandlers.onBlur}
                >
                  {inputProps => (
                    <input {...inputProps} style={inputStyle} type="tel" required />
                  )}
                </InputMask>
              </div>

              {/* ── CPF: idem ── */}
              <div>
                <FieldLabel required>CPF</FieldLabel>
                <InputMask
                  mask="999.999.999-99"
                  value={newFuncionarioData.cpf || ''}
                  onChange={handleLocalInputChange}
                  name="cpf"
                  onFocus={maskFocusHandlers.onFocus}
                  onBlur={maskFocusHandlers.onBlur}
                >
                  {inputProps => (
                    <input {...inputProps} style={inputStyle} type="text" required />
                  )}
                </InputMask>
              </div>

              <div style={{ gridColumn: 'span 2' }}>
                <FieldLabel required>Email</FieldLabel>
                <input
                  style={inputStyle} type="email" name="email"
                  value={newFuncionarioData.email}
                  onChange={handleLocalInputChange} required
                  {...focusHandlers}
                />
              </div>
            </div>

            {/* ── Perfil de Acesso ── */}
            {!isProfileSelectDisabled && (
              <>
                <SectionTitle icon={ICONS.lock} label="Perfil de Acesso" />
                <div style={{ marginBottom: '14px', maxWidth: '320px' }}>
                  <FieldLabel>Perfil</FieldLabel>
                  <select
                    style={selectStyle}
                    name="perfil_unificado_id"
                    value={
                      newFuncionarioData.standard_role_id
                        ? `padrao|${newFuncionarioData.standard_role_id}`
                        : newFuncionarioData.entidade_perfil_id
                          ? `customizado|${newFuncionarioData.entidade_perfil_id}`
                          : ''
                    }
                    onChange={handlePerfilChange}
                    {...focusHandlers}
                  >
                    <option value="">
                      {isEditingFuncionario &&
                       !newFuncionarioData.entidade_perfil_id &&
                       !newFuncionarioData.standard_role_id
                        ? 'Nenhum perfil vinculado'
                        : 'Selecione um Perfil'}
                    </option>
                    {(perfis || [])
                      .filter(p => !(p.tipo === 'padrao' && p.key === 'representante_legal'))
                      .filter(p => {
                        const sectorObj = setores.find(
                          s => s.id?.toString() === selectedSetorModal?.toString()
                        );
                        const isCC = !!(sectorObj && sectorObj.nome === NOME_SETOR_CORPO_CLINICO);
                        if (p.tipo === 'padrao' && p.nome === NOME_PERFIL_MEDICO) return isCC;
                        return true;
                      })
                      .filter(p =>
                        !isProfileSelectDisabled || (perfilMedico && p.id === perfilMedico.id)
                      )
                      .map(p => (
                        <option key={`${p.tipo}-${p.id}`} value={`${p.tipo}|${p.id}`}>
                          {p.nome} ({p.tipo === 'padrao' ? 'Padrão' : 'Customizado'})
                        </option>
                      ))
                    }
                  </select>
                </div>
              </>
            )}

            {/* ── Remuneração e Comissões ── */}
            <SectionTitle icon={ICONS.info} label="Remuneração e Comissões" />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', marginBottom: '14px', alignItems: 'flex-end' }}>
              <div style={{ minWidth: '180px' }}>
                <FieldLabel>Salário Base (R$)</FieldLabel>
                <CurrencyInput
                  id="salario_base_modal"
                  name="salario_base"
                  style={inputStyle}
                  value={newFuncionarioData.salario_base?.toString() || ''}
                  onValueChange={handleSalaryChange}
                  placeholder="R$ 0,00"
                  allowDecimals decimalsLimit={2}
                  decimalSeparator="," groupSeparator="."
                  prefix="R$ "
                  onFocus={focusHandlers.onFocus}
                  onBlur={focusHandlers.onBlur}
                />
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', paddingBottom: '2px' }}>
                <SwitchField
                  id="comissionavel-switch"
                  label="Comissão Padrão"
                  checked={newFuncionarioData.comissionavel || false}
                  onChange={e => setNewFuncionarioData(prev => ({ ...prev, comissionavel: e.target.checked }))}
                />
                <SwitchField
                  id="comissionavel-origem-switch"
                  label="Comissão por Indicação"
                  checked={newFuncionarioData.comissionavel_origem || false}
                  onChange={e => setNewFuncionarioData(prev => ({ ...prev, comissionavel_origem: e.target.checked }))}
                />
                {currentUserRole === 'Representante Legal' && (
                  <SwitchField
                    id="gerente-switch"
                    label="Funcionário Gerente?"
                    checked={newFuncionarioData.is_gerente || false}
                    onChange={e => setNewFuncionarioData(prev => ({ ...prev, is_gerente: e.target.checked }))}
                  />
                )}
              </div>
            </div>

            {/* ── Acesso ao Sistema ── */}
            <SectionTitle icon={ICONS.lock} label="Acesso ao Sistema" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px', marginBottom: '20px' }}>
              <div>
                <FieldLabel required={!isEditingFuncionario}>
                  Senha{isEditingFuncionario ? ' (em branco = sem alteração)' : ''}
                </FieldLabel>
                <input
                  style={inputStyle} type="password" name="password"
                  value={newFuncionarioData.password || ''}
                  onChange={handleLocalInputChange}
                  required={!isEditingFuncionario}
                  minLength={isEditingFuncionario && !newFuncionarioData.password ? undefined : 8}
                  {...focusHandlers}
                />
              </div>
              <div>
                <FieldLabel required={!isEditingFuncionario}>Confirmar Senha</FieldLabel>
                <input
                  style={inputStyle} type="password" name="password_confirmation"
                  value={newFuncionarioData.password_confirmation || ''}
                  onChange={handleLocalInputChange}
                  required={!isEditingFuncionario && !!newFuncionarioData.password}
                  minLength={isEditingFuncionario && !newFuncionarioData.password_confirmation ? undefined : 8}
                  {...focusHandlers}
                />
              </div>
            </div>

            {/* ── Botões ── */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                type="button" onClick={handleCloseFuncionarioModal}
                style={{
                  padding: '9px 18px', borderRadius: '8px',
                  border: '1px solid #e5e7eb', backgroundColor: '#fff',
                  color: '#374151', cursor: 'pointer',
                  fontSize: `calc(13px * var(--font-scale))`, fontWeight: '500', fontFamily: 'inherit',
                }}
              >
                Cancelar
              </button>
              <button
                type="submit" disabled={creatingFuncionario}
                style={{
                  padding: '9px 20px', borderRadius: '8px', border: 'none',
                  background: creatingFuncionario
                    ? '#93c5fd'
                    : 'linear-gradient(135deg, #1e3a5f, #2563eb)',
                  color: '#fff',
                  cursor: creatingFuncionario ? 'not-allowed' : 'pointer',
                  fontSize: `calc(13px * var(--font-scale))`, fontWeight: '600', fontFamily: 'inherit',
                  display: 'flex', alignItems: 'center', gap: '8px',
                  boxShadow: '0 2px 8px rgba(37,99,235,0.3)',
                }}
              >
                {creatingFuncionario && (
                  <Spinner as="span" animation="border" size="sm"
                    style={{ width: '14px', height: '14px', borderWidth: '2px' }} />
                )}
                {creatingFuncionario
                  ? 'Salvando...'
                  : isEditingFuncionario ? 'Salvar Alterações' : 'Criar Funcionário'}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ColaboradoresTab;
